import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import { gameGenreLabel } from '@/lib/game-genres';
import type { MarketplaceListingCard } from '@/lib/api';

/**
 * A game (or other entry point) INSIDE an arche, as a Store card. Opening it
 * goes straight in — `/a/<arc id>/games/wordtrace` — the same place the
 * LitCraft games list sends you, rather than to the arche's Store page.
 */
export function ListingCard({ listing }: { listing: MarketplaceListingCard }) {
  return (
    <Link
      to={listing.launchPath}
      className="group flex w-64 shrink-0 items-start gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {listing.iconUrl ? (
        <ArcheIconImg
          imageRef={listing.iconUrl}
          size={128}
          alt=""
          className="h-14 w-14 shrink-0 rounded-2xl object-cover shadow-sm"
        />
      ) : (
        <DefaultArcheIcon className="h-14 w-14 shrink-0 rounded-2xl shadow-sm" />
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-foreground">{listing.title}</p>
        <p className="truncate text-xs text-muted-foreground">in {listing.archeName}</p>
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{listing.description}</p>
        {listing.isGame && listing.genres.length > 0 && (
          <Badge variant="outline" className="mt-1.5 text-[10px]">
            {gameGenreLabel(listing.genres[0])}
          </Badge>
        )}
      </div>
    </Link>
  );
}

/**
 * Matching listings inside arches, as a strip above a search's arches. Games
 * get their own heading ("Games"); a mix reads as what it is.
 */
export function ListingStrip({
  listings,
  title,
}: {
  listings: MarketplaceListingCard[];
  title?: string;
}) {
  if (listings.length === 0) return null;
  const heading = title ?? (listings.every((l) => l.isGame) ? 'Games' : 'Inside arches');
  return (
    <section aria-label={heading} className="mb-6">
      <h2 className="mb-3 text-sm font-semibold text-foreground">{heading}</h2>
      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
        {listings.map((listing) => (
          <ListingCard key={listing.listingId} listing={listing} />
        ))}
      </div>
    </section>
  );
}
